import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  FaOilCan, FaBrain, FaArrowRight, FaChartBar, FaShieldAlt, FaMicrochip
} from 'react-icons/fa';
import UserDashboard from './UserDashboard';
import { useAuth } from '../context/AuthContext';
import InvestPlanCards from './InvestPlanCards';
import PublicTopRankers from './PublicTopRankers';
import MarketTable from './StockMarket';

/* ─── 3-Axis Animated Background Canvas ─── */
function ThreeDBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = window.innerWidth;
    let H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    const onResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W;
      canvas.height = H;
    };
    window.addEventListener('resize', onResize);

    /* ── Grid planes ── */
    const COLS = 18;
    const ROWS = 18;
    let t = 0;

    function project(x, y, z) {
      const fov = 600;
      const cx = W / 2;
      const cy = H / 2;
      const scale = fov / (fov + z);
      return {
        sx: cx + x * scale,
        sy: cy + y * scale,
        scale,
      };
    }

    function drawPlane(yOffset, colorBase, tiltX = 0) {
      const size = 80;
      const halfW = (COLS / 2) * size;
      const halfH = (ROWS / 2) * size;

      for (let row = 0; row < ROWS; row++) {
        for (let col = 0; col < COLS; col++) {
          const bx = col * size - halfW;
          const by = row * size - halfH;
          const bz = 0;

          /* wave on Y axis */
          const wave = Math.sin((col / COLS + t * 0.4) * Math.PI * 2) * 18 +
            Math.cos((row / ROWS + t * 0.3) * Math.PI * 2) * 12;

          const x = bx;
          const y = yOffset + by * Math.cos(tiltX) - bz * Math.sin(tiltX) + wave * 0.4;
          const z = bz * Math.cos(tiltX) + by * Math.sin(tiltX) - 340;

          /* corners of each cell */
          const corners = [
            { x, y, z },
            { x: x + size, y, z },
            { x: x + size, y: y + size, z: z + size * 0.3 },
            { x, y: y + size, z: z + size * 0.3 },
          ];
          const pts = corners.map(c => project(c.x, c.y, c.z));

          const intensity = Math.max(0, Math.min(1, (wave + 18) / 36));
          const alpha = 0.04 + intensity * 0.07;

          ctx.beginPath();
          ctx.moveTo(pts[0].sx, pts[0].sy);
          pts.slice(1).forEach(p => ctx.lineTo(p.sx, p.sy));
          ctx.closePath();
          ctx.strokeStyle = colorBase.replace('{a}', (alpha * 0.9).toFixed(3));
          ctx.lineWidth = 0.6;
          ctx.stroke();

          /* glow cells */
          if (intensity > 0.8) {
            ctx.fillStyle = colorBase.replace('{a}', (0.035).toFixed(3));
            ctx.fill();
          }
        }
      }
    }

    /* ── Floating particles (Z-axis depth dots) ── */
    const PARTICLE_COUNT = 55;
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: (Math.random() - 0.5) * 900,
      y: (Math.random() - 0.5) * 900,
      z: Math.random() * 600 - 300,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      vz: (Math.random() - 0.5) * 0.6,
      r: Math.random() * 1.8 + 0.5,
      gold: Math.random() > 0.55,
    }));

    function drawParticles() {
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;
        if (p.z > 350) p.z = -300;
        if (p.z < -350) p.z = 300;
        if (Math.abs(p.x) > 500) p.vx *= -1;
        if (Math.abs(p.y) > 500) p.vy *= -1;

        const proj = project(p.x, p.y, p.z);
        const size = p.r * proj.scale;
        const alpha = 0.15 + proj.scale * 0.5;
        ctx.beginPath();
        ctx.arc(proj.sx, proj.sy, Math.max(0.5, size), 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(234,179,8,${alpha.toFixed(2)})`
          : `rgba(96,165,250,${(alpha * 0.7).toFixed(2)})`;
        ctx.fill();
      });
    }

    /* ── Axis lines (X, Y, Z) ── */
    function drawAxes() {
      const origin = project(0, 0, -300);
      const xEnd = project(120, 0, -300);
      const yEnd = project(0, -120, -300);
      const zEnd = project(0, 0, -180);

      const drawLine = (from, to, color) => {
        ctx.beginPath();
        ctx.moveTo(from.sx, from.sy);
        ctx.lineTo(to.sx, to.sy);
        ctx.strokeStyle = color;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      };

      drawLine(origin, xEnd, 'rgba(234,179,8,0.25)');   // X – gold
      drawLine(origin, yEnd, 'rgba(147,197,253,0.2)');  // Y – blue
      drawLine(origin, zEnd, 'rgba(255,255,255,0.12)'); // Z – white
    }

    function loop() {
      ctx.clearRect(0, 0, W, H);
      t += 0.004;

      /* Horizontal XZ plane */
      drawPlane(90, 'rgba(234,179,8,{a})', 0.55);

      /* Vertical-ish YZ plane */
      drawPlane(-80, 'rgba(96,165,250,{a})', -0.4);

      drawAxes();
      drawParticles();

      animId = requestAnimationFrame(loop);
    }
    loop();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.85,
      }}
    />
  );
}

/* ─── Theme tokens ─── */
const T = {
  bg: '#050d1a',          // deep navy
  bgCard: '#0a1628',      // slightly lighter navy
  bgCardBorder: '#1a2d4a',
  accent: '#eab308',      // gold / yellow
  accentLight: '#fde68a',
  blue: '#3b82f6',
  blueLight: '#93c5fd',
  textPrimary: '#f0f6ff',
  textSecondary: '#94a3b8',
  textMuted: '#4b6280',
};

/* ─── Small reusable badge ─── */
function Badge({ children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 14px',
      background: 'rgba(234,179,8,0.08)',
      border: '1px solid rgba(234,179,8,0.25)',
      borderRadius: 999,
      color: T.accent,
      fontSize: 10,
      fontWeight: 900,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      marginBottom: 20,
    }}>
      {children}
    </span>
  );
}

/* ─── Gold divider line ─── */
function GoldLine() {
  return (
    <div style={{
      width: 40, height: 2,
      background: `linear-gradient(90deg,${T.accent},transparent)`,
      marginBottom: 20,
    }} />
  );
}

export default function Mainpage() {
  const navigate = useNavigate();
  const { authLoading, isLoggedIn, me } = useAuth();
  const location = useLocation();

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    if (location.pathname === '/plans') {
      setTimeout(() => {
        document.getElementById('plans')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.pathname]);

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
  };
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  /* ── Shared card style ── */
  const card = {
    background: T.bgCard,
    border: `1px solid ${T.bgCardBorder}`,
    borderRadius: 28,
    boxShadow: '0 8px 48px rgba(0,0,0,0.45)',
  };

  /* ── Primary button ── */
  const btnPrimary = {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    padding: '14px 32px',
    background: T.accent,
    color: '#0a0d14',
    fontWeight: 900,
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    border: 'none',
    borderRadius: 14,
    cursor: 'pointer',
    boxShadow: `0 4px 24px rgba(234,179,8,0.35)`,
    transition: 'transform 0.18s,box-shadow 0.18s',
  };

  /* ── Ghost button ── */
  const btnGhost = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '11px 26px',
    background: 'transparent',
    color: T.accent,
    fontWeight: 900,
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    border: `1px solid rgba(234,179,8,0.35)`,
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'border-color 0.18s,background 0.18s',
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
     
      color: T.textPrimary,
      overflowX: 'hidden',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>

      {/* ── Scroll progress bar ── */}
      <motion.div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg,${T.accent},${T.blue})`,
          transformOrigin: 'left',
          scaleX,
          zIndex: 60,
        }}
      />

      {/* ── 3D animated canvas background ── */}
      <ThreeDBackground />

      {/* ── Static radial glow accents ── */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: '-10%', left: '15%',
          width: 'clamp(300px, 50vw, 600px)', height: 'clamp(300px, 50vw, 600px)',
          background: 'radial-gradient(ellipse,rgba(234,179,8,0.07) 0%,transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', bottom: '5%', right: '10%',
          width: 'clamp(250px, 40vw, 500px)', height: 'clamp(250px, 40vw, 500px)',
          background: 'radial-gradient(ellipse,rgba(59,130,246,0.08) 0%,transparent 70%)',
          borderRadius: '50%',
        }} />
      </div>

      {/* ── Main content ── */}
      <div style={{
        position: 'relative', zIndex: 10,
        maxWidth: 1280, margin: '0 auto',
        padding: '0 clamp(16px, 4vw, 24px) 128px',
      }}>

        {!authLoading && isLoggedIn ? (
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <UserDashboard me={me} />
            <PublicTopRankers />
          </motion.div>
        ) : (
          <>
            {/* ══════════════════ 1. HERO ══════════════════ */}
            <section style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', paddingTop: 'clamp(80px, 10vw, 120px)' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', 
                gap: 'clamp(32px, 5vw, 64px)', 
                alignItems: 'center', 
                width: '100%' 
              }}>

                {/* Left */}
                <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
                  <motion.div variants={fadeInUp}>
                    <Badge>Institutional Trading Terminal</Badge>
                  </motion.div>

                  <motion.h1 variants={fadeInUp} style={{
                    fontSize: 'clamp(2.2rem, 6vw, 3.5rem)',
                    fontWeight: 900,
                    lineHeight: 1.1,
                    letterSpacing: '-0.04em',
                    textTransform: 'uppercase',
                    margin: '0 0 20px',
                    color: T.textPrimary,
                  }}>
                    Contract Tiers{' '}
                    <span style={{ color: T.accent }}>$1000</span>
                    <span style={{
                      display: 'block',
                      fontSize: 'clamp(1rem, 3vw, 1.35rem)',
                      color: T.textSecondary,
                      fontWeight: 700,
                      marginTop: 16,
                      letterSpacing: 'normal',
                      textTransform: 'none',
                    }}>
                      Yielding $50 – $100 in daily dividends.
                    </span>
                  </motion.h1>

                  <motion.p variants={fadeInUp} style={{
                    fontSize: 'clamp(0.9rem, 2vw, 0.95rem)',
                    color: T.textSecondary,
                    maxWidth: 460,
                    lineHeight: 1.75,
                    marginBottom: 36,
                  }}>
                    Experience automated execution across Crypto, Oil, and AI-driven markets
                    with our ultra-low latency infrastructure. Verified secured node.
                  </motion.p>

                  <motion.div variants={fadeInUp} style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                    <button
                      onClick={() => navigate('/plans#plans')}
                      style={btnPrimary}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(234,179,8,0.5)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 24px rgba(234,179,8,0.35)'; }}
                    >
                      Activate Node<FaArrowRight style={{ fontSize: 11 }} />
                    </button>
                  </motion.div>
                </motion.div>

                {/* Right – live market feed card */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9 }}
                >
                  <div style={{ ...card, padding: 'clamp(20px, 4vw, 28px)' }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginBottom: 28, paddingBottom: 16,
                      borderBottom: `1px solid ${T.bgCardBorder}`,
                    }}>
                      <span style={{
                        fontSize: 10, fontWeight: 900, color: T.accent,
                        letterSpacing: '0.2em', textTransform: 'uppercase',
                      }}>
                        ● Live Market Feed
                      </span>
                    </div>
                    <div style={{ height: 'clamp(180px, 20vw, 220px)', display: 'flex', alignItems: 'flex-end', gap: 'clamp(2px, 1vw, 5px)' }}>
                      {[40, 70, 45, 90, 65, 80, 30, 95, 100, 85, 60].map((h, i) => (
                        <motion.div
                          key={i}
                          animate={{ height: `${h}%` }}
                          transition={{ duration: 1.2, delay: i * 0.06, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
                          style={{
                            flex: 1,
                            borderRadius: '4px 4px 0 0',
                            background: h > 70
                              ? `linear-gradient(180deg,${T.accent},rgba(234,179,8,0.4))`
                              : `rgba(59,130,246,0.2)`,
                            minWidth: 0,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </section>

            {/* ══════════════════ 2. MARKET TABLE ══════════════════ */}
            <section style={{ paddingTop: 'clamp(40px, 8vw, 80px)', paddingBottom: 'clamp(40px, 8vw, 80px)' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', 
                gap: 'clamp(40px, 5vw, 64px)', 
                alignItems: 'center' 
              }}>
                <div>
                  <Badge>Real-Time Liquidity</Badge>
                  <GoldLine />
                  <h2 style={{
                    fontSize: 'clamp(2rem, 5vw, 3.2rem)',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                    margin: '0 0 16px',
                  }}>
                    Institutional<br />
                    <span style={{ color: T.accent }}>Asset Tracking</span>
                  </h2>
                  <p style={{ color: T.textSecondary, lineHeight: 1.7, maxWidth: 400, marginBottom: 28, fontSize: '0.9rem' }}>
                    Direct bridge to global liquidity providers. Monitor premium asset performance
                    executed by our AI nodes in real-time.
                  </p>
                  <button
                    onClick={() => navigate('/register')}
                    style={btnGhost}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(234,179,8,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Open Trading Account <FaArrowRight />
                  </button>
                </div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  style={{ ...card, overflow: 'hidden' }}
                >
                  {/* MarketTable will inherit its own styles; wrap in dark override */}
                  <div style={{ background: T.bgCard, borderRadius: 28, padding: 4 }}>
                    <MarketTable />
                  </div>
                </motion.div>
              </div>
            </section>

            {/* ══════════════════ 3. AI SUPERIORITY ══════════════════ */}
            <section style={{ paddingTop: 'clamp(20px, 4vw, 40px)', paddingBottom: 'clamp(20px, 4vw, 40px)' }}>
              <div style={{
                ...card,
                padding: 'clamp(24px, 5vw, 72px)',
                position: 'relative',
                overflow: 'hidden',
                borderColor: 'rgba(234,179,8,0.18)',
              }}>
                {/* Decorative chip icon */}
                <motion.div
                  animate={{ y: [0, -18, 0], rotate: [0, 4, 0] }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ position: 'absolute', top: 32, right: 'clamp(10px, 5vw, 40px)', opacity: 0.05 }}
                >
                  <FaMicrochip size={180} color={T.accent} />
                </motion.div>

                {/* Gold top-line accent */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg,${T.accent},transparent)`,
                }} />

                <div style={{ maxWidth: 760, position: 'relative', zIndex: 1 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    color: T.accent,
                    fontSize: 10, fontWeight: 900, letterSpacing: '0.28em',
                    textTransform: 'uppercase', marginBottom: 20,
                  }}>
                    <span style={{ width: 28, height: 1, background: T.accent, display: 'inline-block' }} />
                    Future-Proof Assets
                  </div>

                  <h2 style={{
                    fontSize: 'clamp(1.8rem, 5vw, 3.8rem)',
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    fontStyle: 'italic',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.1,
                    margin: '0 0 24px',
                  }}>
                    The Escalating Cost of{' '}
                    <span style={{ color: T.accent }}>AI Superiority</span>
                  </h2>

                  <p style={{ color: T.textSecondary, fontSize: 'clamp(0.9rem, 2vw, 1rem)', lineHeight: 1.8, marginBottom: 36 }}>
                    In 2017, the cost to train a foundational AI model was approximately <strong style={{ color: T.textPrimary }}>$930</strong>.
                    By 2024, elite models like Gemini Ultra reached a staggering <strong style={{ color: T.accent }}>$191 Million</strong>.
                    This exponential surge in capital requirements proves that AI is the new "Digital Gold." By investing in our system,
                    you secure a stake in this high-tier infrastructure, leveraging computational power that is now far beyond the reach of the general public.
                  </p>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', 
                    gap: 20, 
                    marginBottom: 40 
                  }}>
                    {[
                      { label: 'Predictive Intelligence', value: '94.8% Accuracy', sub: 'Refined by Billion-Dollar Neural Networks' },
                      { label: 'The Capital Moat', value: '$100M+ Entry Barrier', sub: 'Institutional-Grade Asset Execution' },
                    ].map((m, i) => (
                      <motion.div
                        key={i}
                        whileHover={{ y: -4 }}
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(234,179,8,0.15)',
                          borderRadius: 18,
                          padding: '24px 28px',
                          cursor: 'default',
                          transition: 'border-color 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.45)'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.15)'}
                      >
                        <div style={{ color: T.accent, fontSize: 10, fontWeight: 900, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 8 }}>
                          {m.label}
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: T.textPrimary, marginBottom: 6 }}>
                          {m.value}
                        </div>
                        <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>
                          {m.sub}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <button
                    onClick={() => navigate('/plans#plans')}
                    style={btnPrimary}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
                  >
                    Access Institutional AI
                  </button>
                </div>
              </div>
            </section>

            {/* ══════════════════ 4. AI STRATEGY ENGINE ══════════════════ */}
            <section style={{ paddingTop: 'clamp(40px, 8vw, 80px)', paddingBottom: 'clamp(40px, 8vw, 80px)' }}>
              <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 56px' }}>
                <Badge><FaBrain style={{ fontSize: 12 }} /> Neural Network Core</Badge>
                <h2 style={{
                  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  letterSpacing: '-0.035em',
                  margin: 0,
                }}>
                  AI Robotics &amp; Yield
                </h2>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', 
                gap: 24 
              }}>
                {[
                  {
                    icon: <FaChartBar size={44} />,
                    title: 'Predictive Analytics',
                    desc: 'Advanced models leveraging $191M+ in training resources to detect market shifts and volatility before they occur.',
                    delay: 0,
                  },
                  {
                    icon: <FaShieldAlt size={44} />,
                    title: 'Self-Learning Nodes',
                    desc: 'As global investment scales, our neural network evolves, continuously optimizing risk parameters and capital safety.',
                    delay: 0.15,
                  },
                  {
                    icon: <FaBrain size={44} />,
                    title: 'Autonomous Execution',
                    desc: '24/7 high-frequency trading eliminated of human emotion—driven purely by algorithms for consistent, guaranteed performance.',
                    delay: 0.3,
                  },
                ].map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: f.delay, duration: 0.7 }}
                    whileHover={{ y: -12 }}
                    style={{
                      ...card,
                      padding: '36px 28px',
                      textAlign: 'center',
                      borderColor: 'rgba(234,179,8,0.1)',
                      cursor: 'default',
                      transition: 'border-color 0.25s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.35)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(234,179,8,0.1)'}
                  >
                    {/* Floating icon */}
                    <div style={{ marginBottom: 24 }}>
                      <motion.div
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.5, ease: 'easeInOut' }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 88, height: 88, borderRadius: '50%',
                          background: 'rgba(234,179,8,0.08)',
                          border: '1px solid rgba(234,179,8,0.2)',
                          color: T.accent,
                        }}
                      >
                        {f.icon}
                      </motion.div>
                    </div>

                    <h3 style={{
                      fontSize: '1.05rem', fontWeight: 900,
                      textTransform: 'uppercase', letterSpacing: '-0.01em',
                      marginBottom: 12, color: T.textPrimary,
                    }}>
                      {f.title}
                    </h3>
                    <p style={{ color: T.textSecondary, fontSize: '0.875rem', lineHeight: 1.7 }}>
                      {f.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* ══════════════════ 5. PLANS ══════════════════ */}
            <div id="plans" style={{ paddingTop: 40, paddingBottom: 80, position: 'relative' }}>
              <InvestPlanCards />
            </div>

            <PublicTopRankers />
          </>
        )}
      </div>

      {/* ══════════════════ FOOTER ══════════════════ */}
      <footer style={{
        background: '#030810',
        borderTop: `1px solid ${T.bgCardBorder}`,
        paddingTop: 56,
        paddingBottom: 56,
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto', padding: '0 24px',
          textAlign: 'center',
        }}>
          {/* Logo */}
          <div style={{
            fontSize: '2rem', fontWeight: 900, letterSpacing: '-0.04em',
            marginBottom: 24, color: T.textPrimary,
          }}>
            <span style={{ color: T.accent }}>3</span>Workers
          </div>

          {/* Gold divider */}
          <div style={{
            width: 40, height: 2, margin: '0 auto 24px',
            background: `linear-gradient(90deg,transparent,${T.accent},transparent)`,
          }} />

          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.08em' }}>
            © {new Date().getFullYear()} 3Workers.

          </div>
        </div>
      </footer>
    </div>
  );
}